import React, { useState } from 'react';
import { FormattedMessage, injectIntl, FormattedHTMLMessage } from 'react-intl';
import { get } from 'lodash-es';
import moment from 'moment';
import { DateRangePicker } from 'react-dates';
import { formatDateTime, FORMAT_STANDARD_DATE, yearListRender } from '../../../constants/dateFormatting';
import { displayCurrencyOptions } from '../../../utils/currencyDisplayFormat';
import { TransactionStatusIcon, TransactionActionStatusIcon } from '../../../components/TransactionStatusIcon';
import {
  TRANSACTION_NEW_POLICY,
  TRANASCTION_INVOICE,
  TRANSACTION_STATUS_INITIATED,
  TRANSACTION_STATUS_CANCELED,
  TRANSACTION_STATUS_PAID,
  TRANSACTION_STATUS_STARTED,
  TRANSACTION_STATUS_COMPLETED,
  TRANSACTION_STATUS_FAILED,
  TRANSACTION_ACTION_STATUS_REQUIRED,
  TRANSACTION_ACTION_STATUS_DONE,
} from '../../../constants/userTransactions';
import { currencyConversionEuro, EURO_RATE } from '../../../utils/currencyConversion';

const ORDER_ID_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.orderId" />,
  accessor: 'confirmationCode',
};

const TRANSACTION_ID_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 130,
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.transactionId" />,
  accessor: 'transactionReference',
};

const CREATION_TIMESTAMP_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  headerStyle: { fontWeight: '500' },
  style: { fontWeight: '500' },
  width: 250,
  id: 'createdAtInterval',
  accessor: 'createdAt',
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.creationTimestamp" />,
  Cell: rowInfo => <div>{formatDateTime(rowInfo.original.createdAt, false)}</div>,
  Filter: injectIntl(({ filter, onChange, intl }) => {
    const [inputFocused, setInputFocused] = useState(null);
    return (
      <DateRangePicker
        startDateId="createdAtStart"
        endDateId="createdAtEnd"
        readOnly
        showClearDates
        withPortal
        showDefaultInputIcon={false}
        hideKeyboardShortcutsPanel
        startDate={get(filter, 'value.startDate', null) ? moment(filter.value.startDate) : null}
        endDate={get(filter, 'value.endDate', null) ? moment(filter.value.endDate) : null}
        onDatesChange={({ startDate, endDate }) => onChange({ startDate, endDate })}
        firstDayOfWeek={1}
        small
        focusedInput={inputFocused}
        onFocusChange={focusedInput => setInputFocused(focusedInput)}
        displayFormat={FORMAT_STANDARD_DATE}
        startDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeStart' })}
        endDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeEnd' })}
        isOutsideRange={date => date.isAfter(moment(), 'day')}
        initialVisibleMonth={() =>
          (moment(get(filter, 'value.startDate', null)).isValid() && moment(filter.value.startDate)) ||
          (moment(get(filter, 'value.endDate', null)).isValid() && moment(filter.value.endDate)) ||
          moment().subtract(1, 'month')
        }
        renderMonthElement={({ month, onMonthSelect, onYearSelect }) => (
          <div className="d-flex justify-content-center">
            <div className="mr-1">
              <select value={month.month()} onChange={e => onMonthSelect(month, e.target.value)}>
                {moment.months().map((label, value) => (
                  <option value={value} key={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ml-1">
              <select value={month.year()} onChange={e => onYearSelect(month, e.target.value)}>
                {yearListRender(2019, new Date().getFullYear())}
              </select>
            </div>
          </div>
        )}
      />
    );
  }),
};

const ORDER_TYPE_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 100,
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.orderType" />,
  id: 'type',
  accessor: 'type',
  Cell: rowInfo => (
    <div>
      {rowInfo.original.type && (
        <FormattedMessage id={`adminUserTransactionsPage.orderTypeFilter.${rowInfo.original.type}`} />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => onChange(event.target.value)}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.orderTypeFilter.all' })}</option>
      <option value={TRANSACTION_NEW_POLICY}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.orderTypeFilter.NEW_INSURANCE_PAYMENT' })}
      </option>
      <option value={TRANASCTION_INVOICE}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.orderTypeFilter.INVOICE_PAYMENT' })}
      </option>
    </select>
  )),
};

const ORDER_VALUE_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 220,
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.orderValue" />,
  accessor: 'totalAmount',
  Cell: injectIntl(({ intl, ...rowInfo }) => (
    <div>
      {intl.formatNumber(rowInfo.original.totalAmount / 100, displayCurrencyOptions)} |{' '}
      {currencyConversionEuro(rowInfo.original.totalAmount, 0, EURO_RATE, 100)}
    </div>
  )),
};

const ORDER_PLATFORM_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 100,
  id: 'shop',
  accessor: 'shop',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.orderShop" />,
  Cell: rowInfo => (
    <div>
      {rowInfo.original.shop && (
        <FormattedMessage id={`adminUserTransactionsPage.shopFilter.${rowInfo.original.shop}`} />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => onChange(event.target.value)}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.shopFilter.all' })}</option>
      <option value="WEB_APPLICATION">
        {intl.formatMessage({ id: 'adminUserTransactionsPage.shopFilter.WEB_APPLICATION' })}
      </option>
      <option value="MOBILE_APPLICATION">
        {intl.formatMessage({ id: 'adminUserTransactionsPage.shopFilter.MOBILE_APPLICATION' })}
      </option>
    </select>
  )),
};

const USER_NAME_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 180,
  Header: <FormattedMessage id="adminUserTransactionsPage.columns.user" />,
  accessor: 'userName',
};

const ORDER_PAYMENT_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'status',
  accessor: 'status',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.orderPaymentStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      <TransactionStatusIcon status={rowInfo.original.status} className="svg-table-icons" />
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        onChange(event.target.value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.statusFilter.all' })}</option>
      <option value={TRANSACTION_STATUS_INITIATED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.statusFilter.INITIATED' })}
      </option>
      <option value={TRANSACTION_STATUS_CANCELED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.statusFilter.CANCELED' })}
      </option>
      <option value={TRANSACTION_STATUS_PAID}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.statusFilter.PAID' })}
      </option>
    </select>
  )),
};

const ORDER_SAVING_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'processStatus',
  accessor: 'processStatus',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.orderSavingStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      {rowInfo.original.type === TRANSACTION_NEW_POLICY && (
        <TransactionStatusIcon status={rowInfo.original.processStatus} className="svg-table-icons" />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        const value = event.target.value === 'null' ? null : event.target.value;
        onChange(value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.processStatusFilter.all' })}</option>
      <option value={TRANSACTION_STATUS_STARTED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.processStatusFilter.STARTED' })}
      </option>
      <option value={TRANSACTION_STATUS_COMPLETED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.processStatusFilter.COMPLETED' })}
      </option>
      <option value={TRANSACTION_STATUS_FAILED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.processStatusFilter.FAILED' })}
      </option>
      <option value="null">
        {intl.formatMessage({ id: 'adminUserTransactionsPage.processStatusFilter.WAITING' })}
      </option>
    </select>
  )),
};

const ORDER_NOTICE_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'emailStatus',
  accessor: 'emailStatus',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.orderNoticeStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      {rowInfo.original.type === TRANSACTION_NEW_POLICY && (
        <TransactionStatusIcon status={rowInfo.original.emailStatus} className="svg-table-icons" />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        const value = event.target.value === 'null' ? null : event.target.value;
        onChange(value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.emailStatusFilter.all' })}</option>
      <option value={TRANSACTION_STATUS_STARTED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.emailStatusFilter.STARTED' })}
      </option>
      <option value={TRANSACTION_STATUS_COMPLETED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.emailStatusFilter.COMPLETED' })}
      </option>
      <option value={TRANSACTION_STATUS_FAILED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.emailStatusFilter.FAILED' })}
      </option>
      <option value="null">{intl.formatMessage({ id: 'adminUserTransactionsPage.emailStatusFilter.WAITING' })}</option>
    </select>
  )),
};

const ORDER_MEANS_OF_PAYMENT_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'invoiceReceivedStatus',
  accessor: 'invoiceReceivedStatus',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.orderMeansOfPaymentStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      <TransactionStatusIcon status={rowInfo.original.invoiceReceivedStatus} className="svg-table-icons" />
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        const value = event.target.value === 'null' ? null : event.target.value;
        onChange(value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">
        {intl.formatMessage({ id: 'adminUserTransactionsPage.invoiceReceivedStatusFilter.all' })}
      </option>
      <option value={TRANSACTION_STATUS_STARTED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.invoiceReceivedStatusFilter.STARTED' })}
      </option>
      <option value={TRANSACTION_STATUS_COMPLETED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.invoiceReceivedStatusFilter.COMPLETED' })}
      </option>
      <option value={TRANSACTION_STATUS_FAILED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.invoiceReceivedStatusFilter.FAILED' })}
      </option>
      <option value="null">
        {intl.formatMessage({ id: 'adminUserTransactionsPage.invoiceReceivedStatusFilter.WAITING' })}
      </option>
    </select>
  )),
};

const ACTION_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'actionStatus',
  accessor: 'actionStatus',
  Header: <FormattedHTMLMessage id="adminUserTransactionsPage.columns.actionStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      <TransactionActionStatusIcon status={rowInfo.original.actionStatus} className="svg-table-icons" />
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        onChange(event.target.value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUserTransactionsPage.actionStatusFilter.all' })}</option>
      <option value={TRANSACTION_ACTION_STATUS_REQUIRED}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.actionStatusFilter.REQUIRED' })}
      </option>
      <option value={TRANSACTION_ACTION_STATUS_DONE}>
        {intl.formatMessage({ id: 'adminUserTransactionsPage.actionStatusFilter.DONE' })}
      </option>
    </select>
  )),
};

export const columnsShort = [
  ORDER_ID_COLUMN,
  CREATION_TIMESTAMP_COLUMN,
  ORDER_TYPE_COLUMN,
  ORDER_VALUE_COLUMN,
  ACTION_STATUS_COLUMN,
];

export const columns = [
  ORDER_ID_COLUMN,
  TRANSACTION_ID_COLUMN,
  CREATION_TIMESTAMP_COLUMN,
  ORDER_TYPE_COLUMN,
  ORDER_VALUE_COLUMN,
  ORDER_PLATFORM_COLUMN,
  USER_NAME_COLUMN,
  ACTION_STATUS_COLUMN,
  ORDER_PAYMENT_STATUS_COLUMN,
  ORDER_SAVING_STATUS_COLUMN,
  ORDER_NOTICE_STATUS_COLUMN,
  ORDER_MEANS_OF_PAYMENT_STATUS_COLUMN,
];
